<template>
  <div class="alipaycard-root">
    <div v-if="booting" class="page">
      <div class="brand">{{ brandTitle }}</div>
      <div class="sub">加载中…</div>
    </div>

    <div v-show="!booting" class="page">
      <div class="brand">{{ brandTitle }}</div>
      <div class="sub">{{ brandSub }}</div>

      <div class="card">
        <div class="amount-row">
          <span class="currency">¥</span>
          <span class="amount">{{ amountYuan }}</span>
          <button type="button" class="btn-copy" @click="copyText(copyAmountValue)">复制</button>
        </div>
        <p class="amount-hint">{{ amountHint }}</p>

        <div v-if="countdownVisible" class="countdown-row">
          订单剩余 <span class="countdown-val">{{ countdownText }}</span>
        </div>

        <p v-if="statusHint" class="hint" :class="{ err: isErrHint }">{{ statusHint }}</p>

        <div v-show="canPay">
          <div class="field">
            <div class="field-label">收款银行</div>
            <div class="field-row"><span class="field-val">{{ bankLabel || '—' }}</span></div>
          </div>
          <div class="field">
            <div class="field-label">开户名</div>
            <div class="field-row">
              <span class="field-val">{{ accountName || '—' }}</span>
              <button type="button" class="btn-copy" @click="copyText(accountName)">复制</button>
            </div>
          </div>
          <div class="field">
            <div class="field-label">收款银行卡号</div>
            <div class="field-row">
              <span class="field-val">{{ cardNoDisplay || '—' }}</span>
              <button type="button" class="btn-copy" @click="copyText(cardNo)">复制</button>
            </div>
          </div>
          <button type="button" class="btn-jump" :disabled="jumping" @click="onJump">打开支付宝转卡</button>
        </div>

        <div class="meta">
          <div class="meta-row"><span>商户订单号</span><span class="meta-val">{{ mchOrderNo || '—' }}</span></div>
          <div class="meta-row"><span>下单时间</span><span class="meta-val">{{ createTime || '—' }}</span></div>
        </div>
      </div>
    </div>
    <div v-show="toastVisible" class="toast">{{ toastText }}</div>
    <div
      v-if="showPayerNameModal"
      class="payer-name-mask"
      role="dialog"
      aria-modal="true"
      aria-labelledby="payerNameTitle"
    >
      <div class="payer-name-dialog">
        <h3 id="payerNameTitle" class="payer-name-title">请填写真实姓名末字</h3>
        <p class="payer-name-text">请输入付款人<strong>真实姓名</strong>的最后一个字（支付宝实名，不是昵称）</p>
        <input
          v-model="payerNameInput"
          class="payer-name-input"
          type="text"
          maxlength="8"
          inputmode="text"
          autocomplete="off"
          placeholder="真实姓名最后一字，例如：鹏"
          @keyup.enter="onConfirmPayerName"
        />
        <button
          type="button"
          class="payer-name-confirm"
          :disabled="payerNameSubmitting"
          @click="onConfirmPayerName"
        >确定</button>
        <p v-if="payerNameError" class="payer-name-err">{{ payerNameError }}</p>
      </div>
    </div>
  </div>
</template>

<script>
import { fetchAlipayCardCashierPage, submitAlipayCardPayerName } from '@/api/api'

const STYLE_ID = 'alipaycard-pay-stylesheet'
const POLL_MS = 3000
const AMOUNT_HINT = '严格按照展示金额支付，否则无法自动到账'

function formatMmSs (remainSec) {
  const s = Math.max(0, Math.floor(remainSec))
  const m = Math.floor(s / 60)
  const r = s % 60
  return String(m).padStart(2, '0') + ':' + String(r).padStart(2, '0')
}

export default {
  name: 'AlipayCardPay',
  data () {
    return {
      booting: true,
      orderId: '',
      token: '',
      brandTitle: '支付宝转卡',
      brandSub: '请向以下银行卡完成转账',
      amountYuan: '--',
      amountHint: AMOUNT_HINT,
      mchOrderNo: '',
      createTime: '',
      hint: '',
      failReason: '',
      pageLoadFailed: false,
      invalidToken: false,
      expired: false,
      orderPaid: false,
      expireAtMillis: null,
      nowMs: Date.now(),
      bankLabel: '',
      accountName: '',
      cardNo: '',
      cardNoDisplay: '',
      jumpUrl: '',
      jumping: false,
      toastVisible: false,
      toastText: '已复制',
      toastTimer: null,
      tickTimer: null,
      pollTimer: null,
      payerNameFilled: false,
      payerNameInput: '',
      payerNameSubmitting: false,
      payerNameError: ''
    }
  },
  computed: {
    remainSec () {
      if (!this.expireAtMillis) return 0
      return Math.max(0, Math.floor((this.expireAtMillis - this.nowMs) / 1000))
    },
    countdownVisible () {
      return !this.invalidToken && !this.orderPaid && this.expireAtMillis != null && this.remainSec > 0
    },
    countdownText () {
      return formatMmSs(this.remainSec)
    },
    canPay () {
      return !this.invalidToken && !this.expired && !this.orderPaid
        && this.payerNameFilled && !!this.cardNo && !!this.jumpUrl
    },
    showPayerNameModal () {
      return !this.booting && !this.pageLoadFailed && !this.invalidToken
        && !this.expired && !this.orderPaid && !this.payerNameFilled
    },
    isErrHint () {
      return this.invalidToken || this.expired || this.pageLoadFailed
    },
    copyAmountValue () {
      return this.amountYuan && this.amountYuan !== '--' ? this.amountYuan : ''
    },
    statusHint () {
      if (this.pageLoadFailed) {
        return this.failReason || '收银台加载失败，请刷新重试'
      }
      if (this.invalidToken) {
        return this.failReason || '链接无效或已失效，请重新下单'
      }
      if (this.orderPaid) return '支付成功，可关闭本页'
      if (this.expired) return '订单已过期'
      if (!this.payerNameFilled) return '请先填写真实姓名最后一个字后再转账'
      if (!this.cardNo) {
        return this.failReason || '收款卡号暂不可用'
      }
      return this.hint || ''
    }
  },
  mounted () {
    document.title = '支付宝转卡'
    this.ensureStyle()
    this.orderId = String((this.$route.params && this.$route.params.orderId) || '')
    this.token = String((this.$route.query && this.$route.query.t) || '')
    this.boot()
  },
  beforeDestroy () {
    this.clearTimers()
    if (this.toastTimer) {
      clearTimeout(this.toastTimer)
      this.toastTimer = null
    }
  },
  methods: {
    clearTimers () {
      if (this.tickTimer) {
        clearInterval(this.tickTimer)
        this.tickTimer = null
      }
      if (this.pollTimer) {
        clearInterval(this.pollTimer)
        this.pollTimer = null
      }
    },
    ensureStyle () {
      if (typeof document === 'undefined' || document.getElementById(STYLE_ID)) return
      const el = document.createElement('style')
      el.id = STYLE_ID
      el.textContent = `
.alipaycard-root { min-height: 100vh; background: #f5f5f5; }
.alipaycard-root .page { max-width: 420px; margin: 0 auto; padding: 24px 16px 40px; box-sizing: border-box; }
.alipaycard-root .brand { font-size: 18px; font-weight: 600; color: #333; text-align: center; }
.alipaycard-root .sub { font-size: 13px; color: #999; text-align: center; margin-top: 6px; }
.alipaycard-root .card { background: #fff; border-radius: 12px; padding: 24px 20px; margin-top: 20px; }
.alipaycard-root .amount-row { display: flex; align-items: center; justify-content: center; gap: 10px; flex-wrap: wrap; }
.alipaycard-root .currency { font-size: 20px; }
.alipaycard-root .amount { font-size: 36px; font-weight: 600; }
.alipaycard-root .btn-copy {
  height: 32px; padding: 0 12px; border: 0; border-radius: 6px;
  background: #1677ff; color: #fff; font-size: 13px; cursor: pointer; flex-shrink: 0;
}
.alipaycard-root .btn-copy:active { opacity: 0.88; }
.alipaycard-root .amount-hint { text-align: center; color: #f56c6c; font-size: 13px; line-height: 1.5; margin: 12px 0 0; }
.alipaycard-root .countdown-row { text-align: center; margin-top: 10px; color: #666; font-size: 13px; }
.alipaycard-root .countdown-val { color: #1677ff; font-weight: 600; }
.alipaycard-root .hint { text-align: center; color: #666; font-size: 13px; line-height: 1.5; margin: 14px 0; }
.alipaycard-root .hint.err { color: #f56c6c; }
.alipaycard-root .field { margin-top: 16px; padding-top: 16px; border-top: 1px solid #f0f0f0; }
.alipaycard-root .field-label { font-size: 13px; color: #999; }
.alipaycard-root .field-row { display: flex; align-items: center; justify-content: space-between; gap: 8px; margin-top: 6px; }
.alipaycard-root .field-val { font-size: 18px; font-weight: 600; color: #333; word-break: break-all; flex: 1; }
.alipaycard-root .btn-jump {
  display: block; width: 100%; margin-top: 22px; height: 44px;
  border: 0; border-radius: 8px; background: #1677ff; color: #fff; font-size: 16px; cursor: pointer;
}
.alipaycard-root .btn-jump:active { opacity: 0.88; }
.alipaycard-root .btn-jump:disabled { opacity: 0.5; cursor: not-allowed; }
.alipaycard-root .meta { margin-top: 18px; font-size: 12px; color: #999; }
.alipaycard-root .meta-row { display: flex; justify-content: space-between; padding: 4px 0; }
.alipaycard-root .meta-val { color: #666; max-width: 60%; word-break: break-all; text-align: right; }
.alipaycard-root .toast {
  position: fixed; left: 50%; bottom: 80px; transform: translateX(-50%);
  background: rgba(0,0,0,0.72); color: #fff; font-size: 13px;
  padding: 8px 16px; border-radius: 16px; z-index: 9;
}
.alipaycard-root .payer-name-mask {
  position: fixed; inset: 0; z-index: 1000;
  background: rgba(0, 0, 0, 0.45);
  display: flex; align-items: center; justify-content: center;
  padding: 16px; box-sizing: border-box;
}
.alipaycard-root .payer-name-dialog {
  width: min(100%, 320px); background: #fff; border-radius: 8px;
  padding: 20px 16px 16px; box-sizing: border-box;
}
.alipaycard-root .payer-name-title {
  margin: 0 0 12px; font-size: 18px; font-weight: 600; color: #f56c6c;
}
.alipaycard-root .payer-name-text {
  margin: 0 0 12px; font-size: 14px; color: #303133; line-height: 1.5;
}
.alipaycard-root .payer-name-input {
  width: 100%; box-sizing: border-box; height: 48px; padding: 0 12px;
  border: 1px solid #dcdfe6; border-radius: 4px; font-size: 22px; text-align: center;
  letter-spacing: 2px; margin-bottom: 14px;
}
.alipaycard-root .payer-name-confirm {
  width: 100%; height: 40px; border: 0; border-radius: 8px;
  background: #1677ff; color: #fff; font-size: 16px; cursor: pointer;
}
.alipaycard-root .payer-name-confirm:disabled { opacity: 0.5; cursor: not-allowed; }
.alipaycard-root .payer-name-err { margin: 8px 0 0; font-size: 13px; color: #f56c6c; }
@media (max-width: 360px) {
  .alipaycard-root .amount { font-size: 30px; }
  .alipaycard-root .field-val { font-size: 16px; }
}
`
      document.head.appendChild(el)
    },
    async boot () {
      this.booting = true
      try {
        await this.refreshPage(true)
        this.startTimers()
      } finally {
        this.booting = false
      }
    },
    startTimers () {
      this.clearTimers()
      this.tickTimer = setInterval(() => {
        this.nowMs = Date.now()
        if (this.expireAtMillis && this.nowMs >= this.expireAtMillis && !this.orderPaid) {
          this.expired = true
        }
      }, 1000)
      this.pollTimer = setInterval(() => {
        if (this.orderPaid || this.invalidToken || this.pageLoadFailed || !this.payerNameFilled) return
        this.refreshPage(false, true)
      }, POLL_MS)
    },
    async refreshPage (isFirst, statusPoll) {
      try {
        const data = await fetchAlipayCardCashierPage(this.orderId, this.token, !!statusPoll)
        if (!data) {
          if (isFirst) {
            this.pageLoadFailed = true
            this.failReason = '收银台返回空数据'
          }
          return
        }
        this.pageLoadFailed = false
        this.invalidToken = !!data.invalidToken
        this.expired = !!data.expired
        this.orderPaid = !!data.orderPaid
        this.brandTitle = (data.brandTitle && String(data.brandTitle).trim()) || this.brandTitle
        this.brandSub = (data.brandSub && String(data.brandSub).trim()) || this.brandSub
        this.amountYuan = data.amountYuan || this.amountYuan || '--'
        this.amountHint = (data.amountHint && String(data.amountHint).trim()) || this.amountHint || AMOUNT_HINT
        this.mchOrderNo = data.mchOrderNo || this.mchOrderNo
        this.createTime = data.createTime || this.createTime
        this.hint = data.hint || this.hint
        this.failReason = data.failReason || this.failReason
        this.expireAtMillis = data.expireAtMillis != null ? Number(data.expireAtMillis) : this.expireAtMillis
        this.bankLabel = data.bankLabel || this.bankLabel
        this.accountName = (data.accountName && String(data.accountName).trim()) || this.accountName
        this.cardNo = (data.cardNo && String(data.cardNo).trim()) || this.cardNo
        this.cardNoDisplay = data.cardNoDisplay || this.cardNoDisplay || this.cardNo
        this.jumpUrl = (data.jumpUrl && String(data.jumpUrl).trim()) || this.jumpUrl
        if (data.payerNameFilled) {
          this.payerNameFilled = true
        }
      } catch (e) {
        if (isFirst) {
          this.pageLoadFailed = true
          this.failReason = (e && (e.msg || e.message)) || '收银台接口请求失败，请确认 payment 已发布'
        }
      }
    },
    onConfirmPayerName () {
      const raw = String(this.payerNameInput || '').trim()
      const name = raw ? raw.charAt(raw.length - 1) : ''
      if (!name) {
        this.payerNameError = '请输入付款人真实姓名的最后一个字'
        return
      }
      if (this.payerNameSubmitting) return
      this.payerNameError = ''
      this.payerNameSubmitting = true
      submitAlipayCardPayerName(this.orderId, this.token, name)
        .then(() => {
          this.payerNameFilled = true
          this.payerNameSubmitting = false
        })
        .catch((err) => {
          this.payerNameSubmitting = false
          const msg = (err && (err.message || err.msg)) ? String(err.message || err.msg) : '提交失败，请重试'
          this.payerNameError = msg
        })
    },
    copyText (text) {
      const v = String(text || '').trim()
      if (!v || !this.payerNameFilled) return
      const ok = () => this.showToast('已复制')
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(v).then(ok).catch(() => this.copyFallback(v, ok))
      } else {
        this.copyFallback(v, ok)
      }
    },
    copyFallback (v, ok) {
      const ta = document.createElement('textarea')
      ta.value = v
      ta.setAttribute('readonly', 'readonly')
      ta.style.position = 'fixed'
      ta.style.left = '-9999px'
      document.body.appendChild(ta)
      ta.select()
      try {
        document.execCommand('copy')
        ok()
      } catch (e) { /* ignore */ }
      document.body.removeChild(ta)
    },
    showToast (msg) {
      this.toastText = msg || '已复制'
      this.toastVisible = true
      if (this.toastTimer) clearTimeout(this.toastTimer)
      this.toastTimer = setTimeout(() => {
        this.toastVisible = false
      }, 1500)
    },
    onJump () {
      if (this.jumping || !this.canPay) return
      this.jumping = true
      try {
        window.location.href = this.jumpUrl
      } catch (e) { /* ignore */ }
      setTimeout(() => {
        this.jumping = false
      }, 1500)
    }
  }
}
</script>
